import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileDiff, Loader2, Undo2, X } from "lucide-react";
import { useAuth } from "../../lib/auth";

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

export function useCanAct(bdEmail) {
  const { user } = useAuth();
  return user?.role === "admin" || (!!user?.email && user.email.toLowerCase() === (bdEmail || "").toLowerCase());
}

// Shared "reason" form: a textarea, a confirm button and a cancel link.
export function ReasonForm({ label, hint, placeholder, cta, tone = "sky", minLength, onSubmit, onCancel, testid }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = text.trim().length >= minLength;
  const submit = async () => {
    setBusy(true);
    try { await onSubmit(text.trim()); } finally { setBusy(false); }
  };
  const btn = tone === "amber" ? "bg-amber-500 hover:bg-amber-600 text-amber-950" : "bg-[#3EA8DC] hover:bg-[#5FBCE6] text-[#0B1220]";
  return (
    <div className="flex flex-col gap-3 rounded-lg bg-white/5 border border-white/10 p-4" data-testid={testid}>
      <label className="flex flex-col gap-1.5 text-sm text-stone-200">
        <span className="font-semibold">{label}</span>
        {hint && <span className="text-xs text-stone-400">{hint}</span>}
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder={placeholder}
          className="mt-1 rounded-md border border-white/15 bg-[#0B1220] px-3 py-2 text-sm text-stone-100 placeholder:text-stone-500 focus:outline-none focus:border-[#3EA8DC]"
          data-testid={`${testid}-text`} />
      </label>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={submit} disabled={busy || !ok} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-40 transition-colors ${btn}`} data-testid={`${testid}-submit`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {cta}
        </button>
        <button type="button" onClick={onCancel} className="inline-flex items-center gap-1.5 text-sm text-stone-400 hover:text-stone-200" data-testid={`${testid}-cancel`}>
          <X className="w-3.5 h-3.5" /> Cancel
        </button>
        {!ok && text.length > 0 && <span className="text-xs text-stone-500">A little more detail is needed.</span>}
      </div>
    </div>
  );
}

// Executed in the viewer: amend and re-issue; or a pending amendment with its status and a withdraw link.
export function StaffAmendPanel({ data, onChanged }) {
  const flow = data.client_flow || {};
  const canAct = useCanAct(data.bd_email);
  const [open, setOpen] = useState(false);
  const am = flow.amendment;

  if (data.amendment_pending && am) {
    const cancel = async () => {
      if (!window.confirm(`Withdraw version ${am.version}? The previous version becomes the current Statement of Work again.`)) return;
      try {
        await axios.post(`${API}/api/sow/${data.cid}/amend/cancel`, {});
        toast.success("Amendment withdrawn. The previous version is current again.");
        onChanged?.();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not withdraw the amendment.");
      }
    };
    return (
      <div className="flex flex-col gap-3 rounded-lg border border-[#3EA8DC]/30 bg-[#3EA8DC]/10 px-4 py-3 text-sm" data-testid="staff-amend-pending" data-version={am.version}>
        <p className="flex items-center gap-2 font-semibold"><FileDiff className="w-4 h-4 text-[#9FD8F2]" /> Version {am.version} issued by {am.issued_by_name} on {fmt(am.issued_at)}, awaiting the client's signature.</p>
        <p className="text-stone-300 whitespace-pre-line">{am.summary}</p>
        <p className="text-xs text-stone-400">
          The version signed on {fmt(am.supersedes?.signed_at)} remains in force until the client signs version {am.version} and Medicys countersigns it.
          {flow.returned ? " The client's first signature on this version was set aside." : ""}
        </p>
        {canAct && (
          <button type="button" onClick={cancel} className="self-start inline-flex items-center gap-1.5 text-xs text-stone-300 underline underline-offset-2 hover:text-white" data-testid="staff-amend-withdraw">
            <Undo2 className="w-3.5 h-3.5" /> Withdraw this amendment
          </button>
        )}
      </div>
    );
  }

  if (!data.amendable) return null;
  const issue = async (summary) => {
    try {
      await axios.post(`${API}/api/sow/${data.cid}/amend`, { summary });
      toast.success(`Version ${(flow.version || 1) + 1} issued. The client has been emailed and the previous version stays in force until they sign.`);
      setOpen(false);
      onChanged?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && typeof d === "object" ? (d.missing || []).join(", ") : d) || "Could not issue the amendment.");
    }
  };
  return (
    <div className="flex flex-col gap-3" data-testid="staff-amend">
      <p className="text-sm text-stone-300">
        Version {flow.version || 1}, signed by {flow.signed_by} on {fmt(flow.signed_at)}
        {flow.countersigned_at ? ` and countersigned by ${flow.countersigned_by} on ${fmt(flow.countersigned_at)}` : ""}.
        {flow.amended ? ` Amended on ${fmt(flow.amended.issued_at)}.` : ""}
      </p>
      {open ? (
        <ReasonForm
          label="What has changed and why"
          hint="Shown to the client in the email and on the first page of the new document. Update the costs, scope or timings in Cognito first; the document is built from the proposal as it stands when the client signs."
          placeholder="e.g. The sample grows from 30 to 36 oncologists at your request, adding £9,250 to the net value; the deadline moves to 12 March 2027."
          cta={`Issue version ${(flow.version || 1) + 1} for signature`} minLength={10} onSubmit={issue} onCancel={() => setOpen(false)} testid="staff-amend-form" />
      ) : canAct ? (
        <button type="button" onClick={() => setOpen(true)} className="self-start inline-flex items-center gap-2 rounded-full border border-[#3EA8DC]/60 px-4 py-2 text-sm font-semibold text-[#9FD8F2] hover:bg-[#3EA8DC]/10 transition-colors" data-testid="staff-amend-open">
          <FileDiff className="w-4 h-4" /> Amend and re-issue for signature
        </button>
      ) : (
        <p className="text-xs text-stone-400" data-testid="staff-amend-not-allowed">Only the BD owner of this proposal{data.bd_poc ? ` (${data.bd_poc})` : ""} or an admin can issue an amendment.</p>
      )}
    </div>
  );
}
